import React, { useRef, useCallback } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import makeAnimated from 'react-select/animated';
import { useTags } from '@/hooks/useTags';

const animatedComponents = makeAnimated();

const customStyles = {
  control: base => ({
    ...base,
    borderRadius: '4px',
    border: '1px solid #e3e3e3',
    boxShadow: 'none',
    minHeight: '38px',
    fontSize: '13px',
    '&:hover': {
      borderColor: '#5882C1',
    },
  }),
  valueContainer: base => ({
    ...base,
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
  }),
  input: base => ({
    ...base,
    margin: '0px',
    padding: '0px',
    '& input': {
      height: '100% !important',
    },
  }),
  indicatorsContainer: base => ({
    ...base,
    height: '36px',
  }),
  dropdownIndicator: base => ({
    ...base,
    padding: '5px',
  }),
  clearIndicator: base => ({
    ...base,
    padding: '5px',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#658ec6',
    borderRadius: '15px',
    fontSize: '12px',
    color: '#ffffff',
    fontWeight: '400',
    margin: '2px 4px 2px 0',
  }),
  multiValueLabel: (base) => ({
    ...base,
    padding: '1px 2px 1px 8px',
    color: '#ffffff',
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#ffffff',
    borderRadius: '0 13px 13px 0',
    padding: '1px 5px 1px 2px',
    transition: 'background-color 0.3s',
    ':hover': {
      backgroundColor: '#5a7fb2', /* darkened #658ec6 */
      color: '#ffffff',
    },
  }),
  menu: base => ({
    ...base,
    borderRadius: '4px',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
    fontSize: '13px',
    zIndex: 10,
  }),
};

const CustomInput = ({ onExited, in: inProp, ...inputProps }) => {
  const inputId = inputProps.selectProps?.inputId || '';
  return (
    <components.Input {...inputProps} data-cy={String(inputId).toLowerCase()} />
  );
};

const TagField = ({ autoFocus, handleChange, ...props }) => {
  const intl = useIntl();
  const { data: allTags = [], isLoading } = useTags();
  const selectRef = useRef(null);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      if (!selectRef.current?.state.menuIsOpen) {
        event.preventDefault();
      }
    }
  }, []);

  return (
    <CreatableSelect
      ref={selectRef}
      onKeyDown={handleKeyDown}
      classNamePrefix="select-multi-tag"
      styles={customStyles}
      components={{
        ...animatedComponents,
        Input: CustomInput,
      }}
      options={allTags}
      isMulti
      autoFocus={autoFocus}
      isLoading={isLoading}
      placeholder={intl.formatMessage({ id: 'SELECT_A_BOOKMARK' })}
      noOptionsMessage={() => intl.formatMessage({ id: 'NO_BOOKMARK_FOUND_MESSAGE' })}
      formatCreateLabel={value => `${intl.formatMessage({ id: 'ADD' })} "${value}"`}
      onChange={handleChange}
      {...props}
    />
  );
};

TagField.propTypes = {
  autoFocus: PropTypes.bool,
  handleChange: PropTypes.func.isRequired,
  inputId: PropTypes.string,
};

TagField.defaultProps = {
  autoFocus: false,
};

export default TagField;

